import * as React from "react";
import * as ReactDOM from "react-dom";
import EnhancedTextarea from "./EnhancedTextarea";
import warning = require("warning");
import {themeTokens} from "@octopusdeploy/design-system-tokens";
import {baseSizeInPx} from "fontWeights";

const easeOut = (ms = "200ms", prop = "all") => `${prop} ${ms} cubic-bezier(0.23, 1, 0.32, 1)`;

const getStyles = (props: any, state: any) => {
    const floatingLabelColor = themeTokens.color.text.tertiary;
    const focusColor = themeTokens.color.text.link.default;
    const textColor = themeTokens.color.text.primary;
    const disabledTextColor = themeTokens.color.text.disabled;
    const backgroundColor = "transparent";
    const errorColor = themeTokens.color.text.danger;
    const borderColor = themeTokens.color.border.primary;

    const styles: any = {
        root: {
            fontSize: baseSizeInPx,
            lineHeight: "1.5rem",
            width: props.fullWidth ? "100%" : 256,
            height: (props.rows - 1) * 24 + (!props.multiLine && props.floatingLabelText ? 62 : 48),
            display: "inline-block",
            position: "relative",
            backgroundColor,
            transition: easeOut("200ms", "height"),
            cursor: props.disabled ? "not-allowed" : "auto",
            marginTop: 0,
        },
        error: {
            position: "relative",
            bottom: 2,
            fontSize: 12,
            lineHeight: "12px",
            color: errorColor,
            transition: easeOut("200ms", "all"),
        },
        floatingLabel: {
            color: props.disabled ? disabledTextColor : floatingLabelColor,
            pointerEvents: "none",
            position: "absolute",
            top: 28,
            left: 0,
            transformOrigin: "top left",
            transform: (state.hasValue || state.isFocused || props.floatingLabelFixed)
                ? "scale(0.75) translateY(-20px)"
                : "scale(1) translateY(0)",
            transition: easeOut("200ms", "transform"),
            zIndex: 1,
        },
        hint: {
            position: "absolute",
            // Sit below the floated label (which ends ~y=26) and above the underline (y=54).
            top: props.floatingLabelText ? 28 : 14,
            left: 0,
            color: floatingLabelColor,
            pointerEvents: "none",
            opacity: ((!state.hasValue && (!props.floatingLabelText || state.isFocused || props.floatingLabelFixed)) ? 1 : 0),
            transition: easeOut("200ms", "opacity"),
        },
        input: {
            padding: 0,
            position: "relative",
            width: "100%",
            border: "none",
            outline: "none",
            backgroundColor: "rgba(0,0,0,0)",
            color: props.disabled ? disabledTextColor : textColor,
            cursor: "inherit",
            font: "inherit",
            boxSizing: "border-box",
            marginTop: props.floatingLabelText && !props.multiLine ? 0 : undefined,
        },
        inputNative: {
            appearance: "textfield",
            height: "100%",
        },
        textarea: {
            // Align cursor with the hint (top:28) and clear the floated label that ends ~y=26.
            marginTop: props.floatingLabelText ? (props.multiLine ? 28 : 36) : 12,
            marginBottom: props.floatingLabelText ? (props.multiLine ? -28 : -36) : -12,
            boxSizing: "border-box",
            font: "inherit",
        },
        underline: {
            position: "absolute",
            bottom: 8,
            left: 0,
            width: "100%",
            height: 1,
            backgroundColor: props.disabled ? "transparent" : borderColor,
            borderBottom: props.disabled ? "1px dashed" : "none",
            borderColor: props.disabled ? borderColor : undefined,
        },
        underlineFocused: {
            position: "absolute",
            bottom: 8,
            left: 0,
            width: state.isFocused ? "100%" : "0%",
            height: 2,
            backgroundColor: state.errorText ? errorColor : focusColor,
            transition: easeOut("200ms", "width"),
        },
    };

    if (state.isFocused && !state.errorText) {
        styles.floatingLabel.color = focusColor;
    }

    if (state.errorText && state.isFocused) {
        styles.floatingLabel.color = errorColor;
    }

    return styles;
};

function isValid(value: any) {
    return value !== "" && value !== undefined && value !== null && !(Array.isArray(value) && value.length === 0);
}

class TextField extends React.PureComponent<any, any> {
    uniqueId: string;
    input: any;

    static defaultProps = {
        disabled: false,
        floatingLabelFixed: false,
        multiLine: false,
        fullWidth: false,
        type: "text",
        underlineShow: true,
        rows: 1,
    };

    state = {
        isFocused: false,
        errorText: undefined as any,
        hasValue: false,
    };

    UNSAFE_componentWillMount() {
        const {
            children,
            name,
            hintText,
            floatingLabelText,
            id,
        } = this.props as any;

        const propsLeaf = children ? children.props : this.props;

        this.setState({
            errorText: this.props.errorText,
            hasValue: isValid(propsLeaf.value) || isValid(propsLeaf.defaultValue),
        });

        warning(name || hintText || floatingLabelText || id, `We don't have enough information
      to build a robust unique id for the TextField component. Please provide an id or a name.`);

        const uniqueId = `${name}-${hintText}-${floatingLabelText}-${
            Math.floor(Math.random() * 0xFFFF)}`;
        this.uniqueId = uniqueId.replace(/[^A-Za-z0-9-]/gi, "");
    }

    UNSAFE_componentWillReceiveProps(nextProps: any) {
        if (nextProps.disabled && !this.props.disabled) {
            this.setState({
                isFocused: false,
            });
        }

        if (nextProps.errorText !== this.props.errorText) {
            this.setState({
                errorText: nextProps.errorText,
            });
        }

        if (nextProps.children && nextProps.children.props) {
            nextProps = nextProps.children.props;
        }

        if (nextProps.hasOwnProperty("value")) {
            const hasValue = isValid(nextProps.value);

            this.setState({
                hasValue,
            });
        }
    }

    blur() {
        if (this.input) {
            this.getInputNode().blur();
        }
    }

    focus() {
        if (this.input) {
            this.getInputNode().focus();
        }
    }

    select() {
        if (this.input) {
            this.getInputNode().select();
        }
    }

    getValue() {
        return this.input ? this.getInputNode().value : undefined;
    }

    getInputNode() {
        return (this.props.children || this.props.multiLine) ?
            this.input.getInputNode() : ReactDOM.findDOMNode(this.input);
    }

    handleInputBlur = (event: any) => {
        this.setState({isFocused: false});
        if (this.props.onBlur) {
            this.props.onBlur(event);
        }
    };

    handleInputChange = (event: any) => {
        if (!this.props.hasOwnProperty("value")) {
            this.setState({hasValue: isValid(event.target.value)});
        }
        if (this.props.onChange) {
            this.props.onChange(event, event.target.value);
        }
    };

    handleInputFocus = (event: any) => {
        if (this.props.disabled) {
            return;
        }
        this.setState({isFocused: true});
        if (this.props.onFocus) {
            this.props.onFocus(event);
        }
    };

    handleHeightChange = (_event: any, height: any) => {
        let newHeight = height + 24;
        if (this.props.floatingLabelText) {
            newHeight += 24;
        }
        (ReactDOM.findDOMNode(this) as any).style.height = `${newHeight}px`;
    };

    _isControlled() {
        return this.props.hasOwnProperty("value");
    }

    render() {
        const {
            children,
            className,
            disabled,
            errorStyle,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            errorText,
            floatingLabelFixed,
            floatingLabelFocusStyle,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            floatingLabelShrinkStyle,
            floatingLabelStyle,
            floatingLabelText,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            fullWidth,
            hintText,
            hintStyle,
            id,
            inputStyle,
            multiLine,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            onBlur,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            onChange,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            onFocus,
            style,
            type,
            underlineDisabledStyle,
            underlineFocusStyle,
            underlineShow,
            underlineStyle,
            rows,
            rowsMax,
            textareaStyle,
            ...other
        } = this.props as any;

        const styles = getStyles(this.props, this.state);
        const inputId = id || this.uniqueId;

        const errorTextElement = this.state.errorText && (
            <div style={Object.assign({}, styles.error, errorStyle)}>
                {this.state.errorText}
            </div>
        );

        const floatingLabelTextElement = floatingLabelText && (
            <label
                htmlFor={inputId}
                style={Object.assign({}, styles.floatingLabel, floatingLabelStyle, this.state.isFocused ? floatingLabelFocusStyle : null)}
            >
                {floatingLabelText}
            </label>
        );

        const inputProps = {
            id: inputId,
            ref: (elem: any) => this.input = elem,
            disabled: this.props.disabled,
            onBlur: this.handleInputBlur,
            onChange: this.handleInputChange,
            onFocus: this.handleInputFocus,
        };

        const childStyleMerged = Object.assign({}, styles.input, inputStyle);

        let inputElement;
        if (children) {
            inputElement = React.cloneElement(children,
                {
                    ...inputProps,
                    ...children.props,
                    style: Object.assign(childStyleMerged, children.props.style),
                });
        } else {
            inputElement = multiLine ? (
                <EnhancedTextarea
                    style={childStyleMerged}
                    textareaStyle={Object.assign({}, styles.textarea, styles.inputNative, textareaStyle)}
                    rows={rows}
                    rowsMax={rowsMax}
                    hintText={hintText}
                    {...other}
                    {...inputProps}
                    onHeightChange={this.handleHeightChange}
                />
            ) : (
                <input
                    type={type}
                    style={Object.assign({}, styles.inputNative, childStyleMerged)}
                    {...other}
                    {...inputProps}
                />
            );
        }

        let rootProps = {};

        if (children) {
            rootProps = other;
        }

        const hintElement = hintText ? (
            <span style={Object.assign({}, styles.hint, hintStyle)}>{hintText}</span>
        ) : null;

        return (
            <div
                {...rootProps}
                className={className}
                style={Object.assign({}, styles.root, style)}
            >
                {floatingLabelTextElement}
                {hintElement}
                {inputElement}
                {underlineShow ? (
                    <>
                        <div style={Object.assign({}, styles.underline, underlineStyle, disabled ? underlineDisabledStyle : {})} />
                        <div style={Object.assign({}, styles.underlineFocused, underlineFocusStyle)} />
                    </>
                ) : null}
                {errorTextElement}
            </div>
        );
    }
}

export default TextField;
